import type {
  FieldSet,
  Price,
  Product,
  Value,
  Variant,
} from '@scayle/storefront-nuxt'
import {
  getSizeFromVariant,
  getFirstAttributeValue,
  getProductSiblings,
  isInStock,
} from '@scayle/storefront-nuxt'
import { colorConfig } from '~/config/defaultConfigs'
import {
  GIFT_CARD_ORDER_NUMBERS,
  PRODUCT_ARTICLE_TYPE_VALUE_PREMIUM_CLUB,
} from '~/constants/scayle'

const BadgeLabel = {
  NEW: 'new',
  SOLD_OUT: 'sold_out',
  ONLINE_EXCLUSIVE: 'online_exclusive',
  SUSTAINABLE: 'sustainable',
  PREMIUM: 'premium',
  DEFAULT: '',
} as const

type BadgeLabelParamsKeys =
  | 'isNew'
  | 'isSoldOut'
  | 'isOnlineOnly'
  | 'isSustainable'
  | 'isPremium'
type BadgeLabelParams = Partial<Record<BadgeLabelParamsKeys, boolean>>

export const getBadgeLabel = (params: BadgeLabelParams = {}): string => {
  if (!params) {
    return BadgeLabel.DEFAULT
  }
  const { isNew, isSoldOut, isOnlineOnly, isSustainable, isPremium } = params

  if (isNew) {
    return BadgeLabel.NEW
  } else if (isSoldOut) {
    return BadgeLabel.SOLD_OUT
  } else if (isOnlineOnly) {
    return BadgeLabel.ONLINE_EXCLUSIVE
  } else if (isSustainable) {
    return BadgeLabel.SUSTAINABLE
  } else if (isPremium) {
    return BadgeLabel.PREMIUM
  } else {
    return BadgeLabel.DEFAULT
  }
}

export const getColorCodeForId = (id: number) => {
  const colorMap: Record<number, string> = colorConfig.colorId

  if (colorMap[id]) {
    return colorMap[id]
  }

  return '#ffffff'
}

export const getIsQuantityChangeDisabled = (product?: Product) =>
  getFirstAttributeValue(product?.attributes, 'isQuantityChangeDisabled')
    ?.value === 'true'

export const getProductSizeCategories = (product: Product) => {
  return (
    product.variants?.reduce((data, variant) => {
      const sizeCategory = getSizeFromVariant(variant, 'sizeCategory')

      if (
        sizeCategory?.value &&
        data.every((item) => item.value !== sizeCategory.value)
      ) {
        return [...data, sizeCategory]
      }

      return data
    }, [] as Array<Value>) || []
  )
}

export const getLowestPrice = (variants: Variant[]): Price | undefined => {
  return variants.reduce((m: Variant, x: Variant) => {
    if (!isInStock(x)) {
      return m
    }
    return m?.price?.withoutTax <= x?.price?.withoutTax ? m : x
  }, {} as Variant)?.price
}

export const getProductOrderNumber = (
  product?: Product,
): undefined | string => {
  if (!product) {
    return
  }

  const mainOrderNumberList = product.attributes?.mainOrderNumberList?.values

  if (!Array.isArray(mainOrderNumberList) || !mainOrderNumberList) {
    return
  }

  const productOrderNumber = mainOrderNumberList?.find((item: Value) =>
    item?.value?.toLowerCase().startsWith('madeleine'),
  )?.value

  if (productOrderNumber?.startsWith('madeleine')) {
    return productOrderNumber.replace('madeleine_', '').toUpperCase()
  }

  return (
    productOrderNumber ??
    getAdvancedAttributeFirstValue(product, 'mainOrderNumber')?.toString()
  )
}

export const getFormattedOrderNumber = (
  product?: Product,
): undefined | string => {
  const orderNumber = getProductOrderNumber(product)
  if (!orderNumber) {
    return
  }

  const order = orderNumber.toString()
  if (order.length !== 7) {
    if (order.startsWith('madeleine')) {
      return order.replace('madeleine_', '').toUpperCase()
    } else {
      return order
    }
  }

  if (order.startsWith('madeleine')) {
    return order.replace('madeleine_', '').toUpperCase()
  }
  return order.toUpperCase()
}

export const getProductOrderNumberFromVariant = (
  variant?: Variant,
): undefined | string => {
  const values = variant?.attributes?.productOrderNumberList?.values

  if (!Array.isArray(values) || values === undefined) {
    return undefined
  }

  const value = values
    ? values?.find((item: Value) =>
        item?.value?.toLowerCase().startsWith('madeleine_'),
      )?.value
    : Array.isArray(variant?.attributes?.productOrderNumber?.values)
      ? variant?.attributes?.productOrderNumber?.values[0].value
      : variant?.attributes?.productOrderNumber?.values.value

  if (value === undefined) {
    return undefined
  }

  if (value.startsWith('madeleine')) {
    return value.replace('madeleine_', '').toUpperCase()
  }

  return value?.toUpperCase()
}

export const isPremiumClubProduct = (product: Product | null): boolean => {
  const articleTypeValue = getFirstAttributeValue(
    product?.attributes,
    'articleType',
  )?.value

  return articleTypeValue === PRODUCT_ARTICLE_TYPE_VALUE_PREMIUM_CLUB
}

export const isGiftcard = (product: Product | null): boolean => {
  if (!product) {
    return false
  }
  const orderNumber = getProductOrderNumber(product) || ''

  return orderNumber ? GIFT_CARD_ORDER_NUMBERS.includes(orderNumber) : false
}

export const getSiblingsColors = (product: Product) => {
  const colorHex = getProductSiblings(product, 'colorHex')
  const color = getProductSiblings(product, 'color')

  return colorHex
    .map((hex, index) => {
      if (!hex || !hex.colors) {
        return null
      }

      const colors = hex.colors.map((c, i) => ({
        id: c.id,
        label: color[index]?.colors[i]?.label || c.label,
        value: c.value,
      }))

      return {
        ...hex,
        colors,
      }
    })
    .filter((item) => item !== null)
}

export interface Size {
  id: number
  variant: Variant
  sizeCategory: string
  size?: string
}

export interface SizeCategory {
  categoryName: string
  sizes: Size
}

export interface ProductSizes {
  [key: string]: Size[]
}

export const getProductSizesFromVariants = (
  variants: Variant[] = [],
): ProductSizes => {
  const categorySortList: string[] = ['ngroessen', 'kgroessen', 'ekgroessen']

  // Map to Size objects
  const sizes: Size[] = variants.map(
    (variant): Size => ({
      id: variant.id,
      variant,
      sizeCategory: (getFirstAttributeValue(variant.attributes, 'sizeCategory')
        ?.value || 'size') as string,
      size: getFirstAttributeValue(variant.attributes, 'erpSizeKey')?.value,
    }),
  )

  // Sort by category
  sizes.sort((a, b) => {
    const getSortIndex = (category: string) => {
      // - 'ngroessen' should always be first
      if (category === 'ngroessen') return -1
      // - 'ekgroessen' should always be last
      if (category === 'ekgroessen') return 999
      return categorySortList.indexOf(category) !== -1
        ? categorySortList.indexOf(category)
        : 500 // default middle
    }

    return getSortIndex(a.sizeCategory) - getSortIndex(b.sizeCategory)
  })

  // Group by category
  const groupedByCategory = sizes.reduce(
    (result: Record<string, Size[]>, size) => {
      ;(result[size.sizeCategory] = result[size.sizeCategory] || []).push(size)
      return result
    },
    {},
  )

  // Sort sizes within each group
  for (const category in groupedByCategory) {
    groupedByCategory[category].sort((a, b) => {
      const left = a.size
      const right = b.size

      if (!left || !right) return 0

      return parseInt(left, 10) - parseInt(right, 10)
    })
  }

  return groupedByCategory
}

export const getLowestPriceBetweenVariants = (product: Product) =>
  product.variants && getLowestPrice(product.variants)

export const attr = (p: Product | Variant | undefined | null, name: string) =>
  p?.attributes?.[name]?.values as Value | undefined

export const getProductOrderNumberList = (variant: Variant) => {
  const productOrderNumberList =
    variant.attributes?.productOrderNumberList?.values
  return Array.isArray(productOrderNumberList)
    ? productOrderNumberList
    : productOrderNumberList
      ? [productOrderNumberList]
      : []
}

export const getMatchingCupSizeVariant = (
  product: Product,
  orderNumber: string,
) => {
  return product.variants?.find((variant) => {
    const orderNumberListArray = getProductOrderNumberList(variant)

    return orderNumberListArray.find((item: any) => {
      const itemValue = item.value.split('_')[1]
      const orderNumberStriped = orderNumber?.toString().replace('-', '')

      return itemValue.toString() === orderNumberStriped
    })
  })
}

export const isCupSizeVariant = (variant: Variant): boolean => {
  // Extract the size attribute (e.g., "75 B" from "sizeCategorySize")
  const variantAttribute = getFirstAttributeValue(
    variant.attributes,
    'sizeCategorySize',
  )

  if (!variantAttribute?.label) return false

  // Check if the size format matches a cup size
  const labelParts = variantAttribute.label.split('|')
  const sizePart = labelParts[1]?.trim()
  const regex = /^\d+[ ]?[A-Za-z]$/

  return regex.test(sizePart)
}
export const getCupSizeMatchingArticlePlacement = (
  newOrderNumber: string,
  articlePlacements: FieldSet | undefined,
  promotionKey: string | undefined,
  defaultPromotionKey: string,
) => {
  const firstFiveDigits = newOrderNumber.replace('-', '').substring(0, 5)

  const findMatch = (key: string) =>
    articlePlacements?.find((articlePlacement: any) => {
      if (!Array.isArray(articlePlacement)) return false

      const placement = articlePlacement?.flat()[0]?.value?.toString()
      if (!placement) return false

      const [orderNumber, ppk] = placement.split('|')
      return orderNumber.startsWith(firstFiveDigits) && ppk === key
    })

  const matchedPlacement = promotionKey
    ? findMatch(promotionKey)
    : findMatch(defaultPromotionKey)

  if (!matchedPlacement) return undefined

  const flattenedPlacement = matchedPlacement.flat()
  const placementValue = flattenedPlacement?.[0]?.value?.toString()
  console.log('placementValue', placementValue)

  if (!placementValue) return undefined

  const matchedOrderNumber = placementValue.split('|')[0]

  // TODO: Remove this log before production – for testing only
  console.log('Cup size matched article placement:', flattenedPlacement[0])

  return matchedOrderNumber
}

export const getInitialPricePromotionKey = (
  product: Product,
  orderNumber: string | undefined,
): string | undefined => {
  const articlePlacements =
    product.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

  const matchedOrderNumber = articlePlacements?.find(
    (articlePlacement: any) => {
      if (!Array.isArray(articlePlacement)) return undefined

      return (
        articlePlacement?.flat()[0]?.value?.toString().split('|')[0] ===
        orderNumber?.toString()
      )
    },
  )

  // Return the promotion key if a match is found
  if (matchedOrderNumber && matchedOrderNumber[0]?.value) {
    return matchedOrderNumber[0].value.toString().split('|')[1]
  }

  return undefined
}

export const getSiblingOrderNoWithMatchingPromotionKey = (
  product: Product,
  id: number,
  initialPricePromotionKey: string | undefined,
): {
  matchingOrderNumber: string | undefined
  matchingPromotionKey: string | undefined
} => {
  const promotionKey = initialPricePromotionKey || DEFAULT_PRICE_PROMOTION_KEY

  const matchingItem =
    product.siblings?.find((sibling) => sibling.id === id) ||
    (id === product.id ? product : null)

  if (!matchingItem)
    return { matchingOrderNumber: undefined, matchingPromotionKey: undefined }

  const articlePlacements =
    matchingItem?.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

  const matchingPlacement =
    findMatchingPlacement(articlePlacements, promotionKey) ||
    findMatchingPlacement(articlePlacements, DEFAULT_PRICE_PROMOTION_KEY)

  if (!matchingPlacement)
    return { matchingOrderNumber: undefined, matchingPromotionKey: undefined }

  const [extractedOrderNumber, extractedPromotionKey] =
    matchingPlacement.split('|')

  return {
    matchingOrderNumber: extractedOrderNumber,
    matchingPromotionKey: extractedPromotionKey,
  }
}

// Helper function to find matching article placement
const findMatchingPlacement = (
  placements: any,
  key: string,
): string | undefined => {
  const matchingPlacement = placements?.find((placement: any) => {
    if (!Array.isArray(placement)) return undefined

    return placement?.flat()[0]?.value?.toString().split('|')[1] === key
  })
  return matchingPlacement?.[0]?.value?.toString()
}

export const getSizeNote = (product: Product): string => {
  const sizeNoteValue =
    getFirstAttributeValue(product.attributes, 'sizeNote')?.label || ''

  if (!sizeNoteValue) {
    return ''
  }

  if (sizeNoteValue.includes('großer aus')) {
    return 'basket.size_note.runs_large'
  } else if (sizeNoteValue.includes('kleiner aus')) {
    return 'basket.size_note.runs_small'
  } else if (sizeNoteValue.includes('größengetreu aus')) {
    return 'basket.size_note.runs_true_to_size'
  }

  return ''
}
